import { useEffect, useState, useCallback, useMemo } from "react";
import { Search, Trash2, Mail, X, AlertTriangle, FileText, Inbox, User } from "lucide-react";
import { toast } from "sonner";
import {
  adminListQuotes, adminUpdateQuote, adminDeleteQuote, adminSetQuoteNotes, adminCustomerProfile,
} from "../../lib/api";
import { replyInboxQuote } from "../../lib/replyInbox";
import { UrgencyBadge, urgencyLevel, ageLabel, hoursSince, ExportCsvButton, downloadCsv, Info } from "./shared";
import { CustomerProfileModal, InternalNotesEditor } from "./OrdersTab";

export const QUOTE_STATUSES = ["new", "in_review", "quoted", "won", "lost"];

export function QuotesTab({ pw }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [profileEmail, setProfileEmail] = useState(null);
  const [bulkIds, setBulkIds] = useState(() => new Set());
  const [bulkStatus, setBulkStatus] = useState(QUOTE_STATUSES[1] || "in_review");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState("smart");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const reload = useCallback(() => {
    adminListQuotes(pw)
      .then((d) => setItems(Array.isArray(d) ? d : []))
      .catch((e) => toast.error(`Failed to load quotes: ${e?.response?.data?.detail || e.message}`));
  }, [pw]);
  useEffect(reload, [reload]);

  const update = async (id, status) => {
    try {
      const updated = await adminUpdateQuote(id, { status }, pw);
      setItems((arr) => arr.map((q) => q.id === id ? updated : q));
      if (selected?.id === id) setSelected(updated);
      toast.success("Updated");
    } catch { toast.error("Update failed"); }
  };

  const toggleOne = (id) => setBulkIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAll = (ids) => setBulkIds((s) => {
    if (ids.every((id) => s.has(id))) return new Set();
    return new Set(ids);
  });
  const clearBulk = () => setBulkIds(new Set());

  const bulkApplyStatus = async () => {
    const ids = Array.from(bulkIds);
    if (!ids.length) return;
    if (!window.confirm(`Mark ${ids.length} quote(s) as "${bulkStatus.replace("_", " ")}"?`)) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    const results = await Promise.all(ids.map((id) =>
      adminUpdateQuote(id, { status: bulkStatus }, pw)
        .then((u) => ({ id, updated: u }))
        .catch(() => ({ id, error: true }))
    ));
    setItems((arr) => arr.map((q) => {
      const r = results.find((rr) => rr.id === q.id);
      if (r && r.updated) { ok += 1; return r.updated; }
      if (r && r.error) fail += 1;
      return q;
    }));
    setBulkBusy(false);
    clearBulk();
    if (ok) toast.success(`Updated ${ok} quote(s)`);
    if (fail) toast.error(`${fail} failed`);
  };

  const bulkDelete = async () => {
    const ids = Array.from(bulkIds);
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} quote request(s)? This cannot be undone.`)) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    const results = await Promise.all(ids.map((id) =>
      adminDeleteQuote(id, pw).then(() => { ok += 1; return id; }).catch(() => { fail += 1; return null; })
    ));
    const deletedIds = new Set(results.filter(Boolean));
    setItems((arr) => arr.filter((q) => !deletedIds.has(q.id)));
    setBulkBusy(false);
    clearBulk();
    if (ok) toast.success(`Deleted ${ok} quote(s)`);
    if (fail) toast.error(`${fail} failed`);
  };

  // Filter (search + status) → sort → paginate. `sorted` is the full
  // filtered set (select-all + CSV); `paged` is the visible page.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((qr) => {
      if (statusFilter !== "all" && (qr.status || "new") !== statusFilter) return false;
      if (!q) return true;
      return [qr.request_number, qr.name, qr.email, qr.phone, qr.organization, qr.product_type]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [items, query, statusFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (sortKey === "newest") return arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    if (sortKey === "oldest") return arr.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const score = (q) => {
      const lvl = urgencyLevel(q.created_at, q.status === "new");
      return lvl === "stale" ? 2 : lvl === "urgent" ? 1 : 0;
    };
    return arr.sort((a, b) => {
      const s = score(b) - score(a);
      if (s !== 0) return s;
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [filtered, sortKey]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageClamped = Math.min(page, totalPages);
  const paged = useMemo(
    () => sorted.slice((pageClamped - 1) * pageSize, pageClamped * pageSize),
    [sorted, pageClamped, pageSize],
  );
  useEffect(() => { setPage(1); }, [query, statusFilter, sortKey, pageSize]);

  const exportCsv = () => {
    const headers = [
      "request_number", "created_at", "status", "name", "email", "phone",
      "organization", "organization_type", "product_type", "quantity",
      "deadline", "details",
    ];
    const rows = sorted.map((q) => ({
      request_number: q.request_number,
      created_at: q.created_at,
      status: q.status,
      name: q.name,
      email: q.email,
      phone: q.phone || "",
      organization: q.organization || "",
      organization_type: q.organization_type || "",
      product_type: q.product_type || "",
      quantity: q.quantity || 0,
      deadline: q.deadline || "",
      details: q.details || "",
    }));
    const today = new Date().toISOString().slice(0, 10);
    downloadCsv(`tc-quotes-${today}.csv`, headers, rows);
    toast.success(`Exported ${rows.length} quotes`);
  };

  return (
    <>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-3">
        {bulkIds.size > 0 ? (
          <div className="flex flex-wrap items-center gap-3 bg-[#0d0d0d] border border-[var(--tc-red)] px-4 py-2" data-testid="quotes-bulk-action-bar">
            <span className="font-mono text-xs text-zinc-300">{bulkIds.size} selected</span>
            <select
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
              data-testid="quotes-bulk-status-select"
              className="bg-black border border-[#222] px-2 py-1 text-xs font-mono"
            >
              {QUOTE_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
            </select>
            <button
              onClick={bulkApplyStatus}
              disabled={bulkBusy}
              data-testid="quotes-bulk-apply-status-btn"
              className="font-mono text-[11px] tracking-widest px-3 py-1.5 border border-zinc-700 hover:bg-[var(--tc-red)] hover:border-[var(--tc-red)] hover:text-white transition-colors disabled:opacity-50"
            >
              {bulkBusy ? "WORKING…" : "APPLY STATUS"}
            </button>
            <button
              onClick={bulkDelete}
              disabled={bulkBusy}
              data-testid="quotes-bulk-delete-btn"
              className="font-mono text-[11px] tracking-widest px-3 py-1.5 border border-red-900/60 text-red-400 hover:bg-red-900/20 transition-colors disabled:opacity-50"
            >
              DELETE
            </button>
            <button
              onClick={clearBulk}
              data-testid="quotes-bulk-clear-btn"
              className="font-mono text-[11px] tracking-widest text-zinc-500 hover:text-white"
            >
              CLEAR
            </button>
          </div>
        ) : <span />}
        <ExportCsvButton onClick={exportCsv} count={sorted.length} testid="export-quotes-csv" />
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="quotes-filter-bar">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="quotes-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search request #, name, email, org, product…"
            className="w-full bg-black border border-[#222] pl-9 pr-3 py-2 text-xs font-mono text-zinc-200 focus:border-[var(--tc-red)] outline-none"
          />
        </div>
        <select
          data-testid="quotes-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-black border border-[#222] px-2 py-2 text-xs font-mono text-zinc-200"
        >
          <option value="all">All statuses</option>
          {QUOTE_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
        <select
          data-testid="quotes-sort"
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
          className="bg-black border border-[#222] px-2 py-2 text-xs font-mono text-zinc-200"
        >
          <option value="smart">Sort: Smart (urgent first)</option>
          <option value="newest">Sort: Newest</option>
          <option value="oldest">Sort: Oldest</option>
        </select>
        <span className="font-mono text-[11px] text-zinc-500" data-testid="quotes-result-count">{sorted.length} result{sorted.length === 1 ? "" : "s"}</span>
      </div>
      <div className="surface-card overflow-x-auto" data-testid="quotes-grid">
        <table className="w-full text-left">
          <thead className="bg-[#050505] border-b border-[#222]">
            <tr className="font-mono text-[10px] tracking-widest text-zinc-500">
              <th className="p-4 w-10">
                <input
                  type="checkbox"
                  data-testid="quotes-bulk-select-all"
                  checked={sorted.length > 0 && sorted.every((q) => bulkIds.has(q.id))}
                  onChange={() => toggleAll(sorted.map((q) => q.id))}
                  className="accent-[var(--tc-red)] cursor-pointer"
                />
              </th>
              <th className="p-4">REQUEST</th><th className="p-4">CONTACT</th>
              <th className="p-4">TYPE</th><th className="p-4">QTY</th>
              <th className="p-4">STATUS</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-zinc-500">No quote requests yet.</td></tr>}
            {paged.map((q) => {
              const isChecked = bulkIds.has(q.id);
              return (
              <tr key={q.id} className={`border-b border-[#222] hover:bg-[#0d0d0d] ${isChecked ? "bg-[#141414]" : ""}`} data-testid={`quote-${q.id}`}>
                <td className="p-4 w-10">
                  <input
                    type="checkbox"
                    data-testid={`quotes-bulk-select-${q.id}`}
                    checked={isChecked}
                    onChange={() => toggleOne(q.id)}
                    className="accent-[var(--tc-red)] cursor-pointer"
                  />
                </td>
                <td className="p-4 font-mono text-xs">
                  <div>{q.request_number}</div>
                  <div className="mt-1"><UrgencyBadge createdAt={q.created_at} isOpen={q.status === "new"} /></div>
                  <div className="font-mono text-[10px] text-zinc-600 mt-1">{ageLabel(hoursSince(q.created_at))}</div>
                </td>
                <td className="p-4 text-sm">
                  <div className="font-ui font-bold">{q.name}</div>
                  <div className="text-zinc-500 text-xs">{q.email}</div>
                </td>
                <td className="p-4 text-xs">{q.product_type || q.organization_type}</td>
                <td className="p-4 font-mono text-xs">{q.quantity}</td>
                <td className="p-4">
                  <select data-testid={`quote-status-${q.id}`} value={q.status} onChange={(e) => update(q.id, e.target.value)} className="bg-black border border-[#222] px-2 py-1 text-xs font-mono">
                    {QUOTE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="p-4">
                  <button onClick={() => setSelected(q)} className="font-mono text-xs tracking-widest text-zinc-400 hover:text-white">VIEW →</button>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
      {sorted.length > pageSize && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-3" data-testid="quotes-pagination">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] text-zinc-500">Rows:</span>
            <select
              data-testid="quotes-page-size"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="bg-black border border-[#222] px-2 py-1 text-xs font-mono text-zinc-200"
            >
              {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[11px] text-zinc-500" data-testid="quotes-page-info">
              Page {pageClamped} of {totalPages} · {sorted.length} quotes
            </span>
            <button
              data-testid="quotes-prev-page"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pageClamped <= 1}
              className="font-mono text-[11px] tracking-widest px-3 py-1.5 border border-[#222] hover:border-white disabled:opacity-40 transition-colors"
            >
              ← PREV
            </button>
            <button
              data-testid="quotes-next-page"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={pageClamped >= totalPages}
              className="font-mono text-[11px] tracking-widest px-3 py-1.5 border border-[#222] hover:border-white disabled:opacity-40 transition-colors"
            >
              NEXT →
            </button>
          </div>
        </div>
      )}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div onClick={(e) => e.stopPropagation()} className="surface-card max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-[#222] flex justify-between items-start">
              <div>
                <div className="font-mono text-[11px] tracking-widest text-zinc-500">/ QUOTE_REQUEST</div>
                <h3 className="font-display text-3xl mt-1">{selected.request_number}</h3>
                <div className="font-mono text-xs text-zinc-500 mt-1">{new Date(selected.created_at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-2">
                {selected.email && (
                  <a
                    href={replyInboxQuote(selected)}
                    target="_blank" rel="noreferrer"
                    data-testid={`quote-reply-inbox-${selected.id}`}
                    className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-[var(--tc-blue)] text-[var(--tc-blue)] hover:bg-[var(--tc-blue)] hover:text-white transition-colors"
                  >
                    <Inbox size={14} /> Reply
                  </a>
                )}
                {selected.email && (
                  <button
                    onClick={() => setProfileEmail(selected.email)}
                    data-testid={`quote-customer-profile-${selected.id}`}
                    className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-zinc-700 hover:border-white transition-colors"
                  >
                    <User size={14} /> Profile
                  </button>
                )}
                <button onClick={() => setSelected(null)}><X size={18} /></button>
              </div>
            </div>
            <div className="p-6 grid md:grid-cols-2 gap-6">
              <Info label="Name" value={selected.name} />
              <Info label="Email" value={selected.email} />
              <Info label="Phone" value={selected.phone || "—"} />
              <Info label="Organization" value={selected.organization || "—"} />
              <Info label="Organization Type" value={selected.organization_type} />
              <Info label="Product Type" value={selected.product_type} />
              <Info label="Quantity" value={selected.quantity} />
              <Info label="Deadline" value={selected.deadline || "—"} />
            </div>
            <div className="p-6 border-t border-[#222]">
              <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-2">DETAILS</div>
              <p className="text-zinc-300 font-body text-sm whitespace-pre-wrap">{selected.details}</p>
              {(selected.artwork_urls || []).length > 0 && (
                <>
                  <div className="font-mono text-[10px] tracking-widest text-zinc-500 mt-4 mb-2">ARTWORK</div>
                  <div className="flex gap-2 flex-wrap">
                    {selected.artwork_urls.map((u, i) => (
                      <a key={i} href={u} target="_blank" rel="noreferrer" className="w-20 h-20 border border-[#222] block overflow-hidden">
                        <img src={u} alt="" className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                </>
              )}
              <InternalNotesEditor
                initial={selected.internal_notes || ""}
                onSave={(notes) => adminSetQuoteNotes(selected.id, notes, pw)}
                testidPrefix="quote-notes"
              />
            </div>
          </div>
        </div>
      )}
      {profileEmail && <CustomerProfileModal email={profileEmail} pw={pw} onClose={() => setProfileEmail(null)} />}
    </>
  );
}

export default QuotesTab;
